import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/24x24/info.svg';
import {
  Flex,
  FlexColumn,
  InternalLink,
  PageAlert,
} from '@elementary/common-ui/components';
import { dateCompare } from '@elementary/common-ui/utils/datetime';
import { useMutation, useQuery } from '@tanstack/react-query';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { RULE_ID, NEW_VALUE } from '@elementary/common-ui/constants/urlParams';
import {
  DESTINATION_OPTIONS,
  INTEGRATION_OPTIONS,
  ALERT_RULES,
  DIGEST_RULES,
} from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { EnvironmentPermission } from '@elementary/common-ui/types';
import useInvalidateAlertsRelatedData from 'shared/hooks/useInvalidateAlertsRelatedData';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as sharedApi from 'shared/services/api';
import {
  IntegrationType,
  MicrosoftTeamsBotDestinationOptions,
  PagerDutyDestinationOptions,
} from 'shared/types';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import { describeCron } from 'shared/utils/cronUtils';
import { RuleAccordionProps } from 'alertRules/components/RuleAccordion';
import * as digestApi from '../digestRules/services/api';
import { DigestRuleModel } from '../digestRules/types';
import DigestRuleDrawer from '../digestRules/components/digestRuleDrawer/DigestRuleDrawer';
import DigestRulesList from '../digestRules/components/DigestRulesList';
import * as api from '../services/api';
import useReorderAlertRules from '../hooks/useReorderAlertRules';
import { AlertRoutingRuleModel } from '../types';
import {
  alertFiltersToAlertFilterModels,
  alertFiltersToAlertFilterModels as digestFiltersToModels,
} from '../utils/filterConverters';
import AlertRuleDrawer from './alertRuleDrawer/AlertRuleDrawer';
import AlertRulesList from './AlertRulesList';
import Header, { AlertRulesTab } from './Header';

const TAB_PARAM = 'tab';
const DIGEST_TAB_VALUE: AlertRulesTab = 'incidents-digest';
const ALERT_EDIT_PERMISSION: EnvironmentPermission = 'EDIT_ALERT_RULES';
const DIGEST_EDIT_PERMISSION: EnvironmentPermission = 'EDIT_DIGEST_RULES';

const Wrapper = styled(Flex)`
  height: 100%;
  min-width: 1088px;
  justify-content: center;
  padding: 32px 24px;
`;

const Content = styled(FlexColumn)`
  height: 100%;
  width: 1040px;
  min-width: 1040px;
  gap: 32px;
`;

const AlertRules = () => {
  const selectedEnvId = useSelectedEnv();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigateWithEnv();
  const hasEnvPermission = useEnvPermission();
  const [searchParams] = useSearchParams();
  const ruleId = searchParams.get(RULE_ID);
  const tabParam = searchParams.get(TAB_PARAM) as AlertRulesTab | null;
  const selectedTab: AlertRulesTab =
    tabParam === DIGEST_TAB_VALUE && hasEnvPermission('VIEW_DIGEST_RULES')
      ? DIGEST_TAB_VALUE
      : 'alert-rules';

  const [isAlertVisible, setIsAlertVisible] = useState(true);

  const { data: destinationOptions, isLoading: isDestinationOptionsLoading } =
    useQuery([DESTINATION_OPTIONS], () => sharedApi.fetchDestinationOptions());

  const {
    data: integrationOptionsData,
    isLoading: isIntegrationOptionsLoading,
  } = useQuery([INTEGRATION_OPTIONS], () => sharedApi.fetchAlertIntegrations());

  const { invalidateAlertsDataQueries } = useInvalidateAlertsRelatedData();

  const noIntegrations =
    !isIntegrationOptionsLoading && integrationOptionsData?.length === 0;
  const disableCreateRule =
    isIntegrationOptionsLoading || integrationOptionsData?.length === 0;

  const getServiceName = useCallback(
    (integrationType: IntegrationType, serviceId: string) => {
      const isClientResolvedType =
        integrationType === 'pager_duty' ||
        integrationType === 'microsoft_teams_bot';
      if (isClientResolvedType && isDestinationOptionsLoading) return '';

      if (integrationType === 'pager_duty') {
        const serviceName = (
          destinationOptions?.find(
            (destination) => destination.type === 'pager_duty',
          ) as PagerDutyDestinationOptions
        )?.services?.find(
          (service: { id: string; name: string }) => service.id === serviceId,
        )?.name;

        return serviceName || serviceId;
      }

      if (integrationType === 'microsoft_teams_bot') {
        const teamsBotOptions = destinationOptions?.find(
          (destination) => destination.type === 'microsoft_teams_bot',
        ) as MicrosoftTeamsBotDestinationOptions | undefined;
        return (
          teamsBotOptions?.channel_options?.find(
            (option) => option.value === serviceId,
          )?.label ?? serviceId
        );
      }

      return serviceId;
    },
    [destinationOptions, isDestinationOptionsLoading],
  );

  const { data: alertRules, isLoading: alertRulesLoading } = useQuery(
    [ALERT_RULES, selectedEnvId!],
    () => api.fetchAlertRules(selectedEnvId!),
  );

  const isOrderBasedMatching = alertRules?.multi_rule_matching === false;
  const canReorderAlertRules =
    isOrderBasedMatching && hasEnvPermission(ALERT_EDIT_PERMISSION);

  const {
    reorderAlertRules: onAlertRulesReorder,
    isReorderInFlight: isAlertRulesReorderInFlight,
  } = useReorderAlertRules();

  const alertDrawerItem: AlertRoutingRuleModel | undefined = useMemo(() => {
    if (selectedTab !== 'alert-rules' || !ruleId || ruleId === 'new') {
      return undefined;
    }
    const rule = alertRules?.routing_rules.find((r) => r.id === ruleId);
    if (!rule) return undefined;
    return rule;
  }, [alertRules, ruleId, selectedTab]);

  const isCreateNewAlertRule =
    selectedTab === 'alert-rules' && ruleId === 'new';

  const { mutate: onAlertActivateChanged } = useMutation(
    ({ id, activate }: { id: string; activate: boolean }) =>
      activate
        ? api.activateRule(id, selectedEnvId!)
        : api.deactivateRule(id, selectedEnvId!),
    {
      onSuccess: (_, payload) => {
        invalidateAlertsDataQueries();
        openSnackbar({
          message: `Alert rule was ${
            payload.activate ? 'activated' : 'deactivated'
          } successfully!`,
          variant: 'success',
        });
      },
    },
  );

  const { mutate: onAlertDelete } = useMutation(
    ({ id }: { id: string }) => sharedApi.deleteRule(id, selectedEnvId!),
    {
      onSuccess: () => {
        invalidateAlertsDataQueries();
        openSnackbar({
          message: 'Alert rule was deleted successfully!',
          variant: 'success',
        });
      },
      onError: () => {
        openSnackbar({
          message: 'Something went wrong. Alert rule was not deleted.',
          variant: 'error',
        });
      },
    },
  );

  const alertListData: RuleAccordionProps[] = useMemo(
    () =>
      alertRules?.routing_rules.map((r, i) => ({
        index: isOrderBasedMatching ? i + 1 : undefined,
        id: r.id,
        name: r.name,
        channels: r.destinations.map((d) => ({
          name: getServiceName(d.alert_integration_type, d.channel_name),
          type: d.alert_integration_type,
        })),
        active: !!r.active,
        isDefault: !!r.default,
        assetFilters: alertFiltersToAlertFilterModels(r.asset_filters),
        incidentFilters: alertFiltersToAlertFilterModels(r.incident_filters),
        categories: r.alert_categories,
        editPermission: ALERT_EDIT_PERMISSION,
        categoriesGatedByCloudTests: true,
        onEdit: () =>
          navigate(`/alert-rules?${RULE_ID}=${r.id}`, { replace: true }),
        onActivate: (id) => onAlertActivateChanged({ id, activate: true }),
        onDeactivate: (id) => onAlertActivateChanged({ id, activate: false }),
        onDelete: (id) => onAlertDelete({ id }),
      })) || [],
    [
      alertRules,
      isOrderBasedMatching,
      getServiceName,
      onAlertActivateChanged,
      onAlertDelete,
      navigate,
    ],
  );

  const { data: digestRules, isLoading: digestRulesLoading } = useQuery(
    [DIGEST_RULES, selectedEnvId!],
    () => digestApi.fetchDigestRules(selectedEnvId!),
    { enabled: selectedTab === DIGEST_TAB_VALUE },
  );

  const digestDrawerItem: DigestRuleModel | undefined = useMemo(() => {
    if (selectedTab !== DIGEST_TAB_VALUE || !ruleId || ruleId === 'new') {
      return undefined;
    }
    return digestRules?.digest_rules.find((r) => r.id === ruleId);
  }, [digestRules, ruleId, selectedTab]);

  const isCreateNewDigestRule =
    selectedTab === DIGEST_TAB_VALUE && ruleId === 'new';

  const { mutate: onDigestActivateChanged } = useMutation(
    ({ id, activate }: { id: string; activate: boolean }) =>
      activate
        ? digestApi.activateDigestRule(id, selectedEnvId!)
        : digestApi.deactivateDigestRule(id, selectedEnvId!),
    {
      onSuccess: (_, payload) => {
        invalidateAlertsDataQueries();
        openSnackbar({
          message: `Digest rule was ${
            payload.activate ? 'activated' : 'deactivated'
          } successfully!`,
          variant: 'success',
        });
      },
    },
  );

  const { mutate: onDigestDelete } = useMutation(
    ({ id }: { id: string }) => sharedApi.deleteDigestRule(id, selectedEnvId!),
    {
      onSuccess: () => {
        invalidateAlertsDataQueries();
        openSnackbar({
          message: 'Digest rule was deleted successfully!',
          variant: 'success',
        });
      },
      onError: () => {
        openSnackbar({
          message: 'Something went wrong. Digest rule was not deleted.',
          variant: 'error',
        });
      },
    },
  );

  const digestListData: RuleAccordionProps[] = useMemo(
    () =>
      digestRules?.digest_rules
        .sort((a, b) => dateCompare(a.created_at, b.created_at))
        .map((r) => ({
          id: r.id,
          name: r.name,
          channels: r.destinations.map((d) => ({
            name: getServiceName(d.alert_integration_type, d.channel_name),
            type: d.alert_integration_type,
          })),
          cron: describeCron(r.cron),
          active: !!r.active,
          incidentFilters: digestFiltersToModels(r.incident_filters),
          assetFilters: digestFiltersToModels(r.asset_filters),
          categories: r.incident_categories,
          editPermission: DIGEST_EDIT_PERMISSION,
          onEdit: () =>
            navigate(
              `/alert-rules?${TAB_PARAM}=${DIGEST_TAB_VALUE}&${RULE_ID}=${r.id}`,
              { replace: true },
            ),
          onActivate: (id) => onDigestActivateChanged({ id, activate: true }),
          onDeactivate: (id) =>
            onDigestActivateChanged({ id, activate: false }),
          onDelete: (id) => onDigestDelete({ id }),
        })) || [],
    [
      digestRules,
      getServiceName,
      onDigestActivateChanged,
      onDigestDelete,
      navigate,
    ],
  );

  const onTabChange = (tab: AlertRulesTab) => {
    if (tab === DIGEST_TAB_VALUE) {
      navigate(`/alert-rules?${TAB_PARAM}=${DIGEST_TAB_VALUE}`, {
        replace: true,
      });
    } else {
      navigate('/alert-rules', { replace: true });
    }
  };

  const onDrawerOpen = () => {
    if (selectedTab === DIGEST_TAB_VALUE) {
      navigate(
        `/alert-rules?${TAB_PARAM}=${DIGEST_TAB_VALUE}&${RULE_ID}=${NEW_VALUE}`,
        { replace: true },
      );
    } else {
      navigate(`/alert-rules?${RULE_ID}=${NEW_VALUE}`, { replace: true });
    }
  };

  useViewLoadTracking({
    name:
      selectedTab === 'alert-rules' ? 'Alert rules list' : 'Digest rules list',
    loadDeps: [selectedTab === 'alert-rules' ? alertRules : digestRules],
  });

  return (
    <FlexColumn>
      {noIntegrations && isAlertVisible && (
        <PageAlert icon={<InfoIcon />} onClose={() => setIsAlertVisible(false)}>
          <InternalLink to="/settings/environments">Connect</InternalLink> your
          messaging app or incident management service to start getting alerts
          from Elementary.
        </PageAlert>
      )}

      <Wrapper>
        <Content>
          <Header
            disabledCreate={disableCreateRule}
            onDrawerOpen={onDrawerOpen}
            selectedTab={selectedTab}
            onTabChange={onTabChange}
            showDigestTab={hasEnvPermission('VIEW_DIGEST_RULES')}
          />

          {selectedTab === 'alert-rules' && (
            <AlertRulesList
              data={alertListData}
              isLoading={alertRulesLoading}
              isOrderBasedMatching={isOrderBasedMatching}
              onReorder={canReorderAlertRules ? onAlertRulesReorder : undefined}
              isReorderDisabled={isAlertRulesReorderInFlight}
            />
          )}

          {selectedTab === DIGEST_TAB_VALUE && (
            <DigestRulesList
              data={digestListData}
              isLoading={digestRulesLoading}
            />
          )}
        </Content>

        <AlertRuleDrawer
          open={isCreateNewAlertRule || !!alertDrawerItem}
          onClose={() => navigate('/alert-rules', { replace: true })}
          envId={selectedEnvId!}
          alertRule={alertDrawerItem}
        />

        <DigestRuleDrawer
          open={isCreateNewDigestRule || !!digestDrawerItem}
          onClose={() =>
            navigate(`/alert-rules?${TAB_PARAM}=${DIGEST_TAB_VALUE}`, {
              replace: true,
            })
          }
          envId={selectedEnvId!}
          digestRule={digestDrawerItem}
        />
      </Wrapper>
    </FlexColumn>
  );
};

export default AlertRules;
